// Service pour gérer les postbacks non-bloquants vers Trackiti
export class PostbackService {
  private static readonly TIMEOUT_MS = 5000 // 5 secondes timeout
  private static readonly MAX_RETRIES = 1 // 1 retry maximum

  /**
   * Envoie un postback d'inscription utilisateur
   * Format: https://parking.trackiti.com/postback/1?cid=EVENT_ID&leads=1
   * Ne fait rien si eventId est vide ou null
   */
  static sendRegistrationPostback(eventId: string): void {
    if (!eventId || eventId.trim() === '') {
      console.log('ℹ️ Pas d\'event_id, postback d\'inscription ignoré')
      return
    }

    const url = `https://trackiti.com/api/postback?clickid=${encodeURIComponent(eventId)}&type=lead`

    // Envoi asynchrone non-bloquant
    setTimeout(() => this.sendSimpleGetRequest(url, 'user_registration'), 0)
  }

  /**
   * Envoie un postback de paiement réussi
   * Format: https://parking.trackiti.com/postback/1?cid=EVENT_ID&subscriptions=1
   * Ne fait rien si eventId est vide ou null
   */
  // Taux de conversion vers USD (approximatifs, mis à jour manuellement)
  private static readonly TO_USD: Record<string, number> = {
    USD: 1,
    EUR: 1.08,
    GBP: 1.27,
    CAD: 0.74,
    AUD: 0.65,
    NZD: 0.60,
  }

  static sendPaymentPostback(eventId: string, amount?: number, currency?: string): void {
    if (!eventId || eventId.trim() === '') {
      console.log('ℹ️ Pas d\'event_id, postback de paiement ignoré')
      return
    }

    let amountUsd = amount ?? 2.50
    if (currency && currency !== 'USD') {
      const rate = this.TO_USD[currency] || 1
      amountUsd = amount != null ? amount * rate : 2.50
    }
    const payout = amountUsd.toFixed(2)
    const url = `https://trackiti.com/api/postback?clickid=${encodeURIComponent(eventId)}&payout=${payout}&type=sub`

    // Envoi asynchrone non-bloquant
    setTimeout(() => this.sendSimpleGetRequest(url, 'payment_completed'), 0)
  }

  /**
   * Envoie une requête GET simple (pour les postbacks ParkByMob)
   */
  private static async sendSimpleGetRequest(url: string, eventType: string, retryCount: number = 0): Promise<void> {
    try {
      console.log(`📤 Envoi postback ${eventType} vers ${url}`)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_MS)

      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (response.ok) {
        console.log(`✅ Postback ${eventType} envoyé avec succès`)
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

    } catch (error) {
      console.error(`❌ Erreur postback ${eventType}:`, error)

      // Retry une seule fois
      if (retryCount < this.MAX_RETRIES) {
        console.log(`🔄 Retry postback ${eventType} dans 1 seconde...`)
        setTimeout(() => this.sendSimpleGetRequest(url, eventType, retryCount + 1), 1000)
      } else {
        console.warn(`⚠️ Postback ${eventType} abandonné après ${this.MAX_RETRIES} retry(s)`)
      }
    }
  }

  /**
   * Log les configurations actuelles (pour debug)
   */
  static logConfiguration(): void {
    console.log('🔧 Configuration postback ParkByMob:', {
      registrationUrl: 'https://parking.trackiti.com/postback/1?cid=EVENT_ID&leads=1',
      paymentUrl: 'https://parking.trackiti.com/postback/1?cid=EVENT_ID&subscriptions=1',
      timeout: `${this.TIMEOUT_MS}ms`,
      maxRetries: this.MAX_RETRIES
    })
  }
}

export default PostbackService
